import {
  Users,
  Plus,
  UserPlus,
  MoreHorizontal,
  LogOut,
  Trash2,
  Copy,
  ChevronRight,
  RefreshCw,
  Edit,
  Loader,
  LayoutGrid,
  Activity,
  Mail,
  ArrowRight,
  Share2,
  X,
} from 'lucide-react';
import teamsService from '../../services/teamsService';
import { canRegenerateInviteCode, inviteRegenerateCooldownMessage } from '../../utils/inviteCodeCooldown';
import TeamVaultGrants from '../TeamVaultGrants';
import { teamMembersLimit, teamColor } from './teamsTheme';
import {
  ago,
  activityDesc,
  activityIcon,
  activityStatSub,
  shortAgo,
  GROUP_ORDER,
  groupByDate,
} from './teamsActivity';
import { TeamAvatar } from './TeamsBadges';
import { AccentBtn, Chip, OutlineBtn, SearchInput, DropdownItem, IconBtn } from './TeamsControls';
import { TeamCard, QuickActionRow, MemberRow, LibraryCard, BoardCard } from './TeamsCards';
import { VAULT_TAB, tabsForTeam } from './teamsCache';

/** The hub, team detail and per-tab views. Plain function: it runs during render and holds no hooks. */
export function teamsViews({
  t,
  T,
  teams,
  onOpenModal,
  showToast,
  onOpenWorkspace,
  onShareWithTeam,
  onForkWorkspace,
  onOpenBoard,
  isDark,
  loading,
  error,
  searchTerm,
  setSearchTerm,
  roleFilter,
  setRoleFilter,
  uniqueMembers,
  setDetailTeamId,
  activeTab,
  setActiveTab,
  members,
  membersLoading,
  memberSearch,
  setMemberSearch,
  memberRoleFilter,
  setMemberRoleFilter,
  invitations,
  invLoading,
  setShowInviteModal,
  teamWs,
  wsLoading,
  teamBoards,
  boardsLoading,
  activities,
  actLoading,
  actSearch,
  setActSearch,
  actFilter,
  setActFilter,
  headerMenu,
  setHeaderMenu,
  memberRowMenu,
  setMemberRowMenu,
  selectedMembers,
  setSelectedMembers,
  selectAllRef,
  headerMenuRef,
  activeTeam,
  canAdministerVault,
  isOwner,
  isAdmin,
  canInvite,
  canShareToTeam,
  loadTeams,
  loadInvitations,
  closeMemberRowMenu,
  openTeam,
  copyCode,
  ask,
  changeRole,
  resendInv,
  listTeams,
  filteredMembers,
  filteredAct,
  canChangeRole,
  canRemove,
}) {
  // ═══════════════════════════════════════════════════════════════════════════════
  // HUB
  // ═══════════════════════════════════════════════════════════════════════════════
  const renderHub = () => (
    <div style={{flex:1, width:'100%', height:'100%', backgroundColor:T.bg, overflowY:'auto'}}>
      <div style={{maxWidth:900, margin:'0 auto', padding:'48px 24px 48px'}}>

        {/* Title row */}
        <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, marginBottom:20}}>
          <div>
            <h1 style={{margin:0, fontSize:22, fontWeight:700, color:T.text, letterSpacing:'-0.3px'}}>Teams</h1>
            {!loading && (
              <p style={{margin:'4px 0 0', fontSize:13, color:T.textMuted}}>
                {teams.length} {teams.length===1?'team':'teams'}
                {uniqueMembers>0 && ` · ${uniqueMembers} unique members`}
              </p>
            )}
          </div>
          <div style={{display:'flex', alignItems:'center', gap:8, flexShrink:0}}>
            <OutlineBtn onClick={()=>onOpenModal('join')} icon={UserPlus} T={T}>Join with code</OutlineBtn>
            <AccentBtn onClick={()=>onOpenModal('create')} icon={Plus}>Create team</AccentBtn>
          </div>
        </div>

        {/* Search + filter chips */}
        <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:20, flexWrap:'wrap'}}>
          <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search teams" width={280} T={T}/>
          {[['owned','Owned by me'],['admin','Admin'],['member','Member']].map(([k,label])=>(
            <Chip key={k} label={label} active={roleFilter===k} onClick={()=>setRoleFilter(f=>f===k?null:k)} T={T}/>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div style={{display:'flex', justifyContent:'center', padding:'80px 0'}}>
            <span style={{
              display:'block', width:26, height:26, borderRadius:'50%',
              border:`2.5px solid ${isDark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.08)'}`,
              borderTopColor:'var(--accent)',
              animation:'apb-spin .7s linear infinite',
            }}/>
          </div>
        ) : error ? (
          <div style={{borderRadius:12, border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.06)', padding:20, textAlign:'center'}}>
            <p style={{margin:0, fontSize:13, color:T.red}}>{error}</p>
            <button type="button" onClick={loadTeams}
              style={{marginTop:8, fontSize:12, color:T.red, textDecoration:'underline', background:'none', border:'none', cursor:'pointer'}}>
              Try again
            </button>
          </div>
        ) : listTeams.length===0 ? (
          <div style={{
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
            minHeight:'min(420px, calc(100vh - 220px))', padding:'24px 0', textAlign:'center',
          }}>
            <Users size={36} style={{color:T.chipBorder, marginBottom:16}}/>
            <p style={{margin:0, fontWeight:500, color:T.textSub}}>{searchTerm||roleFilter?'No teams found':'No teams yet'}</p>
            <p style={{margin:'4px 0 0', fontSize:13, color:T.textMuted}}>{searchTerm||roleFilter?'Adjust your search or filters':'Create or join a team to collaborate'}</p>
          </div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            {listTeams.map(team=>{
              const role = isOwner(team)?'owner':(team.role||'member');
              return (
                <TeamCard key={team.id} team={team} role={role}
                  pending={team.pending_invites_count||0}
                  lastActive={ago(team.last_active_at||team.updated_at)}
                  onClick={()=>openTeam(team)} T={T}/>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════════
  // DETAIL SHELL
  // ═══════════════════════════════════════════════════════════════════════════════
  const renderDetail = () => {
    const membersCount = activeTeam?.members_count ?? members.length;
    const wsCount      = teamWs.length;
    const boardsCount  = teamBoards.length;
    const visibleTabs  = tabsForTeam(canAdministerVault);
    return (
      <div style={{flex:1, width:'100%', height:'100%', backgroundColor:T.bg, overflowY:'auto'}}>
        <div style={{maxWidth:900, margin:'0 auto', padding:'40px 24px 48px'}}>

          {/* Breadcrumb */}
          <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:16, fontSize:13}}>
            <button type="button" onClick={()=>setDetailTeamId(null)}
              style={{background:'none', border:'none', cursor:'pointer', color:T.textMuted, fontSize:13, padding:0, fontFamily:'inherit'}}>
              Teams
            </button>
            <ChevronRight size={13} style={{color:T.chipBorder}}/>
            <span style={{color:T.textSub, fontWeight:500}}>{activeTeam.name}</span>
          </div>

          {/* Header */}
          <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, marginBottom:20}}>
            <div style={{display:'flex', alignItems:'center', gap:16, minWidth:0}}>
              <TeamAvatar team={activeTeam} size={56}/>
              <div style={{minWidth:0}}>
                <h1 style={{margin:0, fontSize:20, fontWeight:700, color:T.text}}>{activeTeam.name}</h1>
                <div style={{display:'flex', flexWrap:'wrap', alignItems:'center',
                  gap:'4px 12px', marginTop:4, fontSize:12, color:T.textMuted}}>
                  <span>Owner <span style={{color:T.textSub, fontWeight:500}}>{activeTeam.owner?.name}</span></span>
                  {activeTeam.invite_code && (
                    <span style={{display:'flex', alignItems:'center', gap:6}}>
                      Invite code
                      <span style={{fontFamily:'monospace', fontWeight:700,
                        letterSpacing:'0.12em', color:T.textSub, fontSize:13}}>{activeTeam.invite_code}</span>
                      <button type="button" onClick={()=>copyCode(activeTeam)}
                        style={{background:'none', border:'none', cursor:'pointer', color:T.textMuted, padding:2, lineHeight:1}}>
                        <Copy size={12}/>
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{display:'flex', alignItems:'center', gap:8, flexShrink:0}}>
              {canInvite(activeTeam) && <AccentBtn onClick={()=>setShowInviteModal(true)} icon={UserPlus}>Invite</AccentBtn>}
              {isAdmin(activeTeam) && <OutlineBtn onClick={()=>onOpenModal('edit',activeTeam)} icon={Edit} T={T}>Edit team</OutlineBtn>}
              <div style={{position:'relative'}} ref={headerMenuRef}>
                <IconBtn onClick={()=>setHeaderMenu(o=>!o)} T={T}>
                  <MoreHorizontal size={16}/>
                </IconBtn>
                {headerMenu && (
                  <div style={{
                    position:'absolute', right:0, top:'100%', marginTop:6, zIndex:50,
                    width:210, borderRadius:12, border:`1px solid ${T.border}`,
                    background:T.card, boxShadow:T.shadow, padding:'4px 0',
                  }}>
                    {isAdmin(activeTeam) && (
                      <DropdownItem icon={RefreshCw} label="Regenerate invite code" T={T}
                        onClick={()=>{
                          setHeaderMenu(false);
                          if (!canRegenerateInviteCode(activeTeam)) {
                            showToast?.(inviteRegenerateCooldownMessage(activeTeam), 'error');
                            return;
                          }
                          ask({
                            type:'regenerate',
                            team:activeTeam,
                            title:'Regenerate invite code',
                            message:'The old code will no longer work. Invite codes can only be regenerated once every 24 hours.',
                            confirmText:'Regenerate',
                          });
                        }}/>
                    )}
                    {!isOwner(activeTeam) && (
                      <DropdownItem icon={LogOut} label="Leave team" danger T={T}
                        onClick={()=>{ setHeaderMenu(false); ask({type:'leave',team:activeTeam,title:`Leave "${activeTeam.name}"?`,message:'You will lose access to this team.',confirmText:'Leave team'}); }}/>
                    )}
                    {isOwner(activeTeam) && (
                      <DropdownItem icon={Trash2} label="Delete team" danger T={T}
                        onClick={()=>{ setHeaderMenu(false); ask({type:'delete',team:activeTeam,title:`Delete "${activeTeam.name}"?`,message:'This action cannot be undone.',confirmText:'Delete team'}); }}/>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tab bar */}
          <div style={{borderBottom:`1px solid ${T.border}`, marginBottom:24}}>
            <div style={{display:'flex'}}>
              {visibleTabs.map(tab=>{
                const count = tab==='Members'?membersCount:tab==='Workspaces'?wsCount:tab==='Boards'?boardsCount:null;
                const active = activeTab===tab;
                return (
                  <button key={tab} type="button" onClick={()=>setActiveTab(tab)} style={{
                    position:'relative', display:'flex', alignItems:'center', gap:6,
                    padding:'12px 16px', fontSize:13, fontWeight:500,
                    color: active ? T.text : T.textMuted,
                    background:'none', border:'none', cursor:'pointer', fontFamily:'inherit',
                  }}>
                    {tab}
                    {count!=null && (
                      <span style={{
                        borderRadius:99, padding:'1px 7px', fontSize:11,
                        background: active ? (isDark?'rgba(255,255,255,0.1)':T.chip) : T.chip,
                        color: active ? T.textSub : T.textMuted,
                      }}>{count}</span>
                    )}
                    {active && <span style={{position:'absolute', bottom:-1, left:0, right:0, height:2, borderRadius:2, background:'var(--accent)'}}/>}
                  </button>
                );
              })}
            </div>
          </div>

          {activeTab==='Overview'   && renderOverview()}
          {activeTab==='Members'    && renderMembers()}
          {activeTab==='Workspaces' && renderWorkspaces()}
          {activeTab==='Boards'     && renderBoards()}
          {activeTab==='Activity'   && renderActivity()}
          {/* Also gated on canAdministerVault, not only on the tab being
              reachable: `activeTab` survives switching between teams, so a user
              sitting on the Vault tab of a team they administer must not carry
              it into one they do not. */}
          {activeTab===VAULT_TAB && canAdministerVault && (
            <TeamVaultGrants teamId={activeTeam.id} members={members} />
          )}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // OVERVIEW TAB
  // ═══════════════════════════════════════════════════════════════════════════════
  const renderOverview = () => {
    const totalRequests = teamWs.reduce((s,w)=>s+(w.requests_count||0),0);
    const pendingCount  = activeTeam?.pending_invites_count ?? invitations.length;
    const lastAct       = activities[0];
    const lastActAt     = lastAct?.created_at || activeTeam?.last_active_at;
    const stats = [
      { Icon:LayoutGrid, value:teamWs.length, label:'Workspaces',     sub:`${totalRequests} requests` },
      { Icon:Users,      value:(() => { const c = activeTeam?.members_count??members.length; const lim = teamMembersLimit(activeTeam?.owner?.plan); return lim ? `${c} / ${lim}` : c; })(), label:'Members', sub:`${members.filter(m=>m.role!=='viewer').length} active today` },
      // `ago()` returns German — an English word concatenated around it renders
      // "oldest vor 3 Std.". The phrase, not the timestamp, owns the key.
      { Icon:Mail,       value:pendingCount, label:'Pending invites', sub:pendingCount>0?t('teams.oldestInviteAgo',{ time: ago(invitations[invitations.length-1]?.created_at)||'—' }):'None pending' },
      { Icon:Activity,   value:shortAgo(lastActAt), label:'Last activity', sub:activityStatSub(lastAct) },
    ];
    return (
      <div style={{display:'flex', flexDirection:'column', gap:16}}>
        <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12}}>
          {stats.map(({Icon,value,label,sub})=>(
            <div key={label} style={{borderRadius:12, border:`1px solid ${T.border}`, background:T.card, padding:16}}>
              <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
                <Icon size={15} style={{color:T.textMuted, flexShrink:0}}/>
                <span style={{fontSize:24, fontWeight:700, color:T.text, lineHeight:1}}>{value}</span>
              </div>
              <p style={{margin:0, fontSize:12, fontWeight:500, color:T.textSub}}>{label}</p>
              <p style={{margin:'2px 0 0', fontSize:11, color:T.textMuted,
                overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>{sub}</p>
            </div>
          ))}
        </div>

        <div style={{display:'grid', gridTemplateColumns:'1fr auto', gap:12}}>
          {/* Recent Activity */}
          <div style={{borderRadius:12, border:`1px solid ${T.border}`, background:T.card, padding:20}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16}}>
              <span style={{fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:T.textMuted}}>Recent Activity</span>
              <button type="button" onClick={()=>setActiveTab('Activity')}
                style={{display:'flex', alignItems:'center', gap:4, fontSize:11, color:T.textMuted,
                  background:'none', border:'none', cursor:'pointer', fontFamily:'inherit'}}>
                View all <ArrowRight size={11}/>
              </button>
            </div>
            {actLoading ? (
              <div style={{display:'flex', justifyContent:'center', padding:'32px 0'}}>
                <Loader size={20} style={{color:T.textMuted}} className="animate-spin"/>
              </div>
            ) : activities.length===0 ? (
              <p style={{textAlign:'center', fontSize:12, color:T.textMuted, padding:'24px 0', margin:0}}>No activity yet</p>
            ) : (
              <div style={{display:'flex', flexDirection:'column', gap:14}}>
                {activities.slice(0,4).map(a=>{
                  const Icon=activityIcon(a.type);
                  return (
                    <div key={a.id} style={{display:'flex', alignItems:'flex-start', gap:12}}>
                      <div style={{width:24, height:24, borderRadius:'50%', background:T.chip,
                        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1}}>
                        <Icon size={11} style={{color:T.textSub}}/>
                      </div>
                      <p style={{flex:1, minWidth:0, margin:0, fontSize:12, lineHeight:1.6, color:T.textSub}}>{activityDesc(a,T)}</p>
                      <span style={{flexShrink:0, fontSize:11, color:T.textMuted, whiteSpace:'nowrap'}}>{ago(a.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div style={{borderRadius:12, border:`1px solid ${T.border}`, background:T.card, padding:20, width:260}}>
            <p style={{margin:'0 0 16px', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:T.textMuted}}>Quick Actions</p>
            <div style={{display:'flex', flexDirection:'column', gap:2}}>
              {[
                canInvite(activeTeam)&&{Icon:UserPlus,title:'Invite members',sub:'Send email or share link',onClick:()=>setShowInviteModal(true)},
                canShareToTeam(activeTeam)&&{Icon:Share2,title:t('teamLibrary.shareWorkspace'),sub:t('teamLibrary.shareWorkspaceSub'),onClick:()=>{ setActiveTab('Workspaces'); onShareWithTeam?.(activeTeam); }},
                {Icon:Copy,title:'Copy invite code',sub:activeTeam?.invite_code,onClick:()=>copyCode(activeTeam)},
                isAdmin(activeTeam)&&{Icon:Edit,title:'Edit team',sub:'Name, avatar, description',onClick:()=>onOpenModal('edit',activeTeam)},
              ].filter(Boolean).map(({Icon,title,sub,onClick})=>(
                <QuickActionRow key={title} Icon={Icon} title={title} sub={sub} onClick={onClick} T={T}/>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // MEMBERS TAB
  // ═══════════════════════════════════════════════════════════════════════════════
  const renderMembers = () => {
    const allSelected = filteredMembers.length > 0 && filteredMembers.every(m => selectedMembers.has(m.id));
    const someSelected = !allSelected && filteredMembers.some(m => selectedMembers.has(m.id));
    const selCount = filteredMembers.filter(m => selectedMembers.has(m.id)).length;

    // sync indeterminate DOM property
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;

    const toggleAll = () => {
      if (allSelected) {
        setSelectedMembers(prev => { const n = new Set(prev); filteredMembers.forEach(m => n.delete(m.id)); return n; });
      } else {
        setSelectedMembers(prev => { const n = new Set(prev); filteredMembers.forEach(m => n.add(m.id)); return n; });
      }
    };
    const toggleOne = id => setSelectedMembers(prev => {
      const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
    });

    const canRemoveSelected = filteredMembers.filter(m => selectedMembers.has(m.id) && canRemove(m));
    const bulkRemove = () => {
      if (!canRemoveSelected.length) return;
      const names = canRemoveSelected.map(m => m.name).join(', ');
      ask({
        type: 'bulkRemove',
        team: activeTeam,
        member: canRemoveSelected,
        title: `Remove ${canRemoveSelected.length} member${canRemoveSelected.length > 1 ? 's' : ''}?`,
        message: `Remove ${names} from the team?`,
        confirmText: 'Remove',
      });
    };

    return (
    <div style={{display:'flex', flexDirection:'column', gap:20}}>
      <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
        <SearchInput value={memberSearch} onChange={setMemberSearch} placeholder="Search members" width={240} T={T}/>
        {['owner','admin','editor','viewer'].map(r=>(
          <Chip key={r} label={r} active={memberRoleFilter===r} onClick={()=>setMemberRoleFilter(f=>f===r?null:r)} T={T}/>
        ))}
        <div style={{flex:1}}/>
        {selCount > 0 && (
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            <span style={{fontSize:13, color:T.textSub}}>{selCount} selected</span>
            {canRemoveSelected.length > 0 && (
              <button type="button" onClick={bulkRemove} style={{
                display:'inline-flex', alignItems:'center', gap:6, borderRadius:8,
                padding:'5px 12px', fontSize:13, fontWeight:500, cursor:'pointer',
                background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)',
                color:T.red, fontFamily:'inherit',
              }}>
                <Trash2 size={13}/> Remove
              </button>
            )}
            <button type="button" onClick={()=>setSelectedMembers(new Set())} style={{
              fontSize:12, color:T.textMuted, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit',
            }}>
              Clear
            </button>
          </div>
        )}
        {canInvite(activeTeam) && <AccentBtn onClick={()=>setShowInviteModal(true)} icon={UserPlus}>Invite</AccentBtn>}
      </div>

      <div style={{borderRadius:12, border:`1px solid ${T.border}`, background:T.card, overflow:'hidden'}}>
        {membersLoading ? (
          <div style={{display:'flex', justifyContent:'center', padding:'48px 0'}}>
            <Loader size={22} style={{color:T.textMuted}} className="animate-spin"/>
          </div>
        ) : (
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${T.border}`}}>
                <th style={{width:44, padding:'10px 16px'}}>
                  <input ref={selectAllRef} type="checkbox"
                    className="checkbox-brand flex-shrink-0 cursor-pointer"
                    checked={allSelected} onChange={toggleAll}/>
                </th>
                {['MEMBER','ROLE','JOINED','LAST ACTIVE',''].map(h=>(
                  <th key={h} style={{padding:'10px 16px', textAlign:'left', fontSize:11,
                    fontWeight:600, letterSpacing:'0.06em', color:T.textMuted, whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((m,i)=>{
                const mc = teamColor({name:m.name});
                return (
                  <MemberRow key={m.id} m={m} mc={mc} isLast={i===filteredMembers.length-1}
                    selected={selectedMembers.has(m.id)} onToggle={()=>toggleOne(m.id)}
                    canChangeRole={canChangeRole(m)} canRemove={canRemove(m)}
                    onChangeRole={role=>changeRole(m,role)}
                    onRemove={()=>ask({type:'removeMember',team:activeTeam,member:m,title:'Remove member',message:`Remove ${m.name} from the team?`,confirmText:'Remove'})}
                    menuOpen={memberRowMenu===m.id}
                    onMenuToggle={()=>setMemberRowMenu(id=>id===m.id?null:m.id)}
                    onMenuClose={closeMemberRowMenu}
                    T={T}/>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {isAdmin(activeTeam) && (
        <div>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
            <p style={{margin:0, fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', color:T.textMuted}}>
              Pending Invitations
              {invitations.length>0 && <span style={{color:T.amber, marginLeft:6}}>{invitations.length}</span>}
            </p>
            {invitations.length>1 && (
              <button type="button"
                onClick={async()=>{ for(const inv of invitations){try{await teamsService.resendInvitation(activeTeam.id,inv.id)}catch{}} loadInvitations(); showToast?.('All resent','success'); }}
                style={{display:'flex', alignItems:'center', gap:4, fontSize:12, color:T.textMuted,
                  background:'none', border:'none', cursor:'pointer', fontFamily:'inherit'}}>
                <RefreshCw size={11}/> Resend all
              </button>
            )}
          </div>
          {invLoading ? (
            <div style={{display:'flex', justifyContent:'center', padding:'16px 0'}}>
              <Loader size={18} style={{color:T.textMuted}} className="animate-spin"/>
            </div>
          ) : invitations.length===0 ? (
            <p style={{margin:0, fontSize:12, color:T.textMuted}}>No pending invitations.</p>
          ) : (
            <div style={{borderRadius:12, border:`1px solid ${T.border}`, background:T.card, overflow:'hidden'}}>
              {invitations.map((inv,i)=>(
                <div key={inv.id} style={{
                  display:'flex', alignItems:'center', gap:16, padding:'12px 16px',
                  borderBottom: i<invitations.length-1 ? `1px solid ${T.divider}` : 'none',
                }}>
                  <Mail size={14} style={{color:T.textMuted, flexShrink:0}}/>
                  <span style={{flex:1, minWidth:0, fontSize:13, color:T.textSub,
                    overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>{inv.email}</span>
                  <span style={{fontSize:12, color:T.textMuted, flexShrink:0}}>{inv.role||'member'}</span>
                  {/* One flat key, not "Sent" + a styled <span>: `ago()` is German
                      now, so a hardcoded wrapper renders "Sent vor 3 Std.", and a
                      split phrase cannot reorder for another language. Same shape
                      as `teamLibrary.updatedAgo` at the library row. */}
                  <span style={{fontSize:12, color:T.textMuted, whiteSpace:'nowrap', flexShrink:0}}>
                    {t('teams.invitationSentAgo', { time: ago(inv.created_at) || '—' })}
                  </span>
                  <button type="button" onClick={()=>resendInv(inv)}
                    style={{display:'flex', alignItems:'center', gap:4, fontSize:12, color:T.textMuted,
                      background:'none', border:'none', cursor:'pointer', whiteSpace:'nowrap', fontFamily:'inherit'}}>
                    <RefreshCw size={11}/> Resend
                  </button>
                  <button type="button"
                    onClick={()=>ask({type:'cancelInv',team:activeTeam,member:inv,title:'Cancel invitation',message:`Cancel invitation to ${inv.email}?`,confirmText:'Cancel'})}
                    style={{color:T.red, background:'none', border:'none', cursor:'pointer', padding:0, lineHeight:1, flexShrink:0}}>
                    <X size={14}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // WORKSPACES TAB
  // ═══════════════════════════════════════════════════════════════════════════════
  const renderWorkspaces = () => (
      <div style={{display:'flex', flexDirection:'column', gap:16}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:16}}>
          <p style={{margin:0, fontSize:13, color:T.textMuted}}>
            {t('teamLibrary.description')}
          </p>
          {canShareToTeam(activeTeam) && (
            <OutlineBtn onClick={() => onShareWithTeam?.(activeTeam)} icon={Share2} T={T}>
              {t('teamLibrary.shareWorkspace')}
            </OutlineBtn>
          )}
        </div>
        {wsLoading ? (
          <div style={{display:'flex', justifyContent:'center', padding:'48px 0'}}>
            <Loader size={22} style={{color:T.textMuted}} className="animate-spin"/>
          </div>
        ) : teamWs.length === 0 ? (
          <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'48px 0', gap:8, marginTop:24}}>
            <LayoutGrid size={28} style={{color:T.chipBorder}}/>
            <p style={{margin:0, fontSize:13, color:T.textMuted}}>{t('teamLibrary.empty')}</p>
            {canShareToTeam(activeTeam) && (
              <OutlineBtn onClick={() => onShareWithTeam?.(activeTeam)} icon={Share2} T={T}>
                {t('teamLibrary.shareWorkspace')}
              </OutlineBtn>
            )}
          </div>
        ) : (
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
            {teamWs.map(ws=>(
              <LibraryCard
                key={ws.id}
                ws={ws}
                canManage={isAdmin(activeTeam)}
                onOpen={onOpenWorkspace}
                onFork={onForkWorkspace}
                onRemove={(item)=>ask({
                  type:'removeFromTeam',
                  team:activeTeam,
                  member:{ id: item.id, name: item.name },
                  title:t('teamLibrary.removeTitle'),
                  message:t('teamLibrary.removeConfirm', { name: item.name }),
                  confirmText:t('teamLibrary.remove'),
                })}
                T={T}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    );

  const renderBoards = () => (
      <div style={{display:'flex', flexDirection:'column', gap:16}}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:16}}>
          <p style={{margin:0, fontSize:13, color:T.textMuted}}>
            Boards shared with this team. Create or share a board as “Team” from the Task Manager.
          </p>
        </div>
        {boardsLoading ? (
          <div style={{display:'flex', justifyContent:'center', padding:'48px 0'}}>
            <Loader size={22} style={{color:T.textMuted}} className="animate-spin"/>
          </div>
        ) : teamBoards.length === 0 ? (
          <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'48px 0', gap:8, marginTop:24}}>
            <LayoutGrid size={28} style={{color:T.chipBorder}}/>
            <p style={{margin:0, fontSize:13, color:T.textMuted}}>No boards shared with this team yet.</p>
          </div>
        ) : (
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
            {teamBoards.map(board=>(
              <BoardCard
                key={board.id}
                board={board}
                canManage={isAdmin(activeTeam)}
                onOpen={(b)=>onOpenBoard?.(b)}
                onRemove={(item)=>ask({
                  type:'removeBoardFromTeam',
                  team:activeTeam,
                  member:{ id: item.id, name: item.name },
                  title:'Remove board',
                  message:`Remove "${item.name}" from this team's boards?`,
                  confirmText:t('teamLibrary.remove'),
                })}
                T={T}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    );

  // ═══════════════════════════════════════════════════════════════════════════════
  // ACTIVITY TAB
  // ═══════════════════════════════════════════════════════════════════════════════
  const renderActivity = () => {
    const groups = groupByDate(filteredAct);
    return (
      <div style={{display:'flex', flexDirection:'column', gap:16}}>
        <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
          <SearchInput value={actSearch} onChange={setActSearch} placeholder="Filter activity" width={240} T={T}/>
          {[['members','Members'],['workspaces','Workspaces'],['settings','Settings']].map(([k,label])=>(
            <Chip key={k} label={label} active={actFilter===k} onClick={()=>setActFilter(f=>f===k?null:k)} T={T}/>
          ))}
        </div>
        {actLoading ? (
          <div style={{display:'flex', justifyContent:'center', padding:'48px 0'}}>
            <Loader size={22} style={{color:T.textMuted}} className="animate-spin"/>
          </div>
        ) : filteredAct.length===0 ? (
          <div style={{display:'flex', flexDirection:'column', alignItems:'center', padding:'48px 0'}}>
            <Activity size={32} style={{color:T.chipBorder, marginBottom:12}}/>
            <p style={{margin:0, fontSize:13, color:T.textMuted}}>No activity yet</p>
          </div>
        ) : (
          <div style={{borderRadius:12, border:`1px solid ${T.border}`, background:T.card, overflow:'hidden'}}>
            {GROUP_ORDER.filter(g=>groups[g]).map(groupLabel=>(
              <div key={groupLabel}>
                <div style={{padding:'8px 20px', borderBottom:`1px solid ${T.divider}`, background:T.sectionBg}}>
                  <p style={{margin:0, fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:T.textMuted}}>{groupLabel}</p>
                </div>
                {groups[groupLabel].map((a,i,arr)=>{
                  const Icon=activityIcon(a.type);
                  return (
                    <div key={a.id} style={{
                      display:'flex', alignItems:'flex-start', gap:12, padding:'12px 20px',
                      borderBottom: i<arr.length-1 ? `1px solid ${T.divider}` : 'none',
                    }}>
                      <div style={{width:24, height:24, borderRadius:'50%', background:T.chip,
                        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2}}>
                        <Icon size={11} style={{color:T.textSub}}/>
                      </div>
                      <p style={{flex:1, minWidth:0, margin:0, fontSize:13, lineHeight:1.6, color:T.textSub}}>{activityDesc(a,T)}</p>
                      <span style={{flexShrink:0, fontSize:12, color:T.textMuted, whiteSpace:'nowrap'}}>{ago(a.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return { renderHub, renderDetail };
}
